import React, { useState, useEffect, useRef, useCallback } from 'react';
import { getApiBase } from './config/apiBase';
import { authService } from './services/authService';
import RichTextEditor from './components/RichTextEditor';
import LyricAIPopup from './LyricAIPopup';
import JiraTicketLink from './components/JiraTicketLink';

const apiUrl = getApiBase();

const authenticatedFetch = async (url, options = {}) => {
  const token = await authService.getAccessToken();
  return fetch(url, {
    ...options,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });
};

// Reduce the idea's HTML description to plain text so it can be handed to the
// model as context without dragging markup along.
const htmlToText = (html) => String(html || '')
  .replace(/<\s*br\s*\/?>/gi, '\n')
  .replace(/<\/(p|div|li|h[1-6])>/gi, '\n')
  .replace(/<[^>]+>/g, '')
  .replace(/&nbsp;/g, ' ')
  .replace(/&amp;/g, '&')
  .replace(/&lt;/g, '<')
  .replace(/&gt;/g, '>')
  .replace(/&#39;/g, "'")
  .replace(/&quot;/g, '"')
  .replace(/\n{3,}/g, '\n\n')
  .trim();

/**
 * Create-Epic panel for Step 2 of the SDLC flow. Mirrors the "Design Output"
 * document tools: it takes the Jira Product Discovery Idea linked in Step 1 as
 * the reference, lets the user optionally attach a rule file, uses AI to
 * populate the Epic description from the Idea, edits it in the same rich text
 * editor used in the Jira issue manager, then creates the Epic in Jira and links
 * it back into the flow so downstream steps can reference it.
 */
const CreateEpicPanel = ({ flow, selectedProject, selectedModel, userInfo, onEpicCreated }) => {
  const ideaKeys = flow?.linkedTickets?.jpdIdea || [];

  const [selectedIdeaKey, setSelectedIdeaKey] = useState(ideaKeys[0] || '');
  const [idea, setIdea] = useState(null);
  const [ideaLoading, setIdeaLoading] = useState(false);
  const [ideaError, setIdeaError] = useState(null);

  const [summary, setSummary] = useState('');
  const [description, setDescription] = useState('');
  const [hasGenerated, setHasGenerated] = useState(false);

  // Optional rule file: a built-in template (by name) or a user-uploaded JSON.
  const [ruleTemplates, setRuleTemplates] = useState([]);
  const [ruleTemplate, setRuleTemplate] = useState('');
  const [customRules, setCustomRules] = useState(null);
  const [customRulesName, setCustomRulesName] = useState('');
  const rulesInputRef = useRef(null);

  const [generating, setGenerating] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  // Ref to the description editor so the AI chat can read the current selection
  // and replace it (same wiring as the Stories panel).
  const editorRef = useRef(null);

  // Keep the selected idea valid as the linked set changes.
  useEffect(() => {
    if (ideaKeys.length && !ideaKeys.includes(selectedIdeaKey)) setSelectedIdeaKey(ideaKeys[0]);
    if (!ideaKeys.length && selectedIdeaKey) setSelectedIdeaKey('');
  }, [ideaKeys, selectedIdeaKey]);

  // Load the available built-in rule templates once.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await authenticatedFetch(`${apiUrl}/api/templates`);
        const data = await res.json();
        if (!cancelled && data.success && Array.isArray(data.templates)) {
          setRuleTemplates(data.templates);
          if (data.templates.includes('Epic')) setRuleTemplate('Epic');
        }
      } catch {
        /* non-fatal — rule file is optional */
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Load the referenced Idea's summary + description whenever it changes, seed
  // the Epic summary from the Idea title, and load the Idea's description (the
  // PM ticket's data) straight into the editor so the user starts from it.
  const summaryDirty = useRef(false);
  const loadIdea = useCallback(async (key) => {
    if (!key) { setIdea(null); return; }
    setIdeaLoading(true);
    setIdeaError(null);
    try {
      const res = await authenticatedFetch(`${apiUrl}/api/issue/${encodeURIComponent(key)}`);
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || `Failed to load ${key}`);
      const fields = data.issue?.fields || {};
      const loaded = { key, summary: fields.summary || '', description: fields.description || '' };
      setIdea(loaded);
      if (!summaryDirty.current) setSummary(loaded.summary ? `${loaded.summary}` : '');
      // Load the PM ticket's description into the editor to start from.
      setDescription(loaded.description || '');
      setHasGenerated(false);
    } catch (err) {
      setIdeaError(err.message || `Failed to load ${key}`);
      setIdea(null);
    } finally {
      setIdeaLoading(false);
    }
  }, []);

  useEffect(() => { loadIdea(selectedIdeaKey); }, [selectedIdeaKey, loadIdea]);

  const handleRulesUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const parsed = JSON.parse(String(e.target.result || ''));
        setCustomRules(parsed);
        setCustomRulesName(file.name);
        setError(null);
      } catch {
        setCustomRules(null);
        setCustomRulesName('');
        if (rulesInputRef.current) rulesInputRef.current.value = '';
        setError('That rule file is not valid JSON.');
      }
    };
    reader.onerror = () => setError('Could not read the rule file.');
    reader.readAsText(file);
  };

  const clearCustomRules = () => {
    setCustomRules(null);
    setCustomRulesName('');
    if (rulesInputRef.current) rulesInputRef.current.value = '';
  };

  const buildContext = () => {
    if (!idea) return '';
    const parts = [`Source Jira Product Discovery Idea ${idea.key}: ${idea.summary}`];
    const body = htmlToText(idea.description);
    if (body) parts.push('', body);
    return parts.join('\n');
  };

  const handleGenerate = async () => {
    if (!summary.trim()) { setError('Enter an Epic summary first.'); return; }
    setError(null);
    setResult(null);
    setGenerating(true);
    try {
      const body = {
        summary: summary.trim(),
        issueType: 'Epic',
        selectedModel,
        context: buildContext() || undefined,
        existingDescription: description.trim() ? description : null,
      };
      if (customRules) body.customRules = customRules;
      else if (ruleTemplate) body.rulesTemplate = ruleTemplate;

      const res = await authenticatedFetch(`${apiUrl}/api/ai/generate-description-from-rules`, {
        method: 'POST',
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Failed to generate description');
      setDescription(data.description || '');
      setHasGenerated(true);
    } catch (err) {
      setError(err.message || 'Failed to generate description');
    } finally {
      setGenerating(false);
    }
  };

  const handleCreate = async () => {
    if (!selectedProject) { setError('Select a project at the top of the page first.'); return; }
    if (!summary.trim()) { setError('Enter an Epic summary.'); return; }
    if (!description.trim()) { setError('Populate or write the Epic description first.'); return; }
    setError(null);
    setResult(null);
    setCreating(true);
    try {
      const res = await authenticatedFetch(`${apiUrl}/api/create-epic`, {
        method: 'POST',
        body: JSON.stringify({
          summary: summary.trim(),
          description,
          projectKey: selectedProject,
          selectedModel,
          reporterEmail: userInfo?.email || null,
          reporterName: userInfo?.name || null,
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Failed to create Epic');
      setResult({ epicKey: data.epicKey, epicLink: data.epicLink });
      if (onEpicCreated && data.epicKey) {
        try { await onEpicCreated(data.epicKey); } catch { /* link is best-effort */ }
      }
    } catch (err) {
      setError(err.message || 'Failed to create Epic');
    } finally {
      setCreating(false);
    }
  };

  if (!ideaKeys.length) {
    return (
      <div style={s.hintBox}>
        Link a <strong>Jira Product Discovery Idea</strong> in Step 1 first — the Epic is populated from it.
      </div>
    );
  }

  return (
    <div style={s.wrap}>
      <div style={s.refNote}>
        Populate a new Epic from the linked Idea using AI, edit it, then create it in{' '}
        <strong>{selectedProject || '— select a project —'}</strong>. The created Epic is linked back into this flow.
      </div>

      {/* Source Idea */}
      <label style={s.label}>Source Idea (Jira Product Discovery)</label>
      {ideaKeys.length > 1 ? (
        <select style={s.input} value={selectedIdeaKey} onChange={(e) => setSelectedIdeaKey(e.target.value)}>
          {ideaKeys.map((k) => <option key={k} value={k}>{k}</option>)}
        </select>
      ) : (
        <div style={s.readonly}>{selectedIdeaKey}</div>
      )}
      {ideaLoading ? (
        <p style={s.help}>⏳ Loading {selectedIdeaKey}…</p>
      ) : ideaError ? (
        <div style={s.error}>{ideaError}</div>
      ) : idea ? (
        <p style={s.help}><strong><JiraTicketLink ticketKey={idea.key} />:</strong> {idea.summary}</p>
      ) : null}

      {/* Epic summary */}
      <label style={{ ...s.label, marginTop: 12 }}>Epic summary</label>
      <input
        style={s.input}
        value={summary}
        onChange={(e) => { summaryDirty.current = true; setSummary(e.target.value); }}
        placeholder="Epic name / summary…"
      />

      {/* Rule file (optional) */}
      <label style={{ ...s.label, marginTop: 12 }}>Rule file (optional)</label>
      <div style={s.ruleRow}>
        <select
          style={{ ...s.input, flex: '1 1 200px' }}
          value={customRules ? '' : ruleTemplate}
          onChange={(e) => setRuleTemplate(e.target.value)}
          disabled={Boolean(customRules)}
        >
          <option value="">— No built-in template —</option>
          {ruleTemplates.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <input ref={rulesInputRef} type="file" accept=".json,application/json" onChange={handleRulesUpload} style={{ display: 'none' }} />
        <button type="button" style={s.secondaryBtn} onClick={() => rulesInputRef.current?.click()}>📎 Upload rule file</button>
      </div>
      {customRulesName && (
        <div style={s.chipWrap}>
          <span style={s.chip}>{customRulesName}<button style={s.chipX} onClick={clearCustomRules} title="Remove">×</button></span>
        </div>
      )}

      {/* Generate */}
      <div style={s.actionRow}>
        <button
          style={{ ...s.primaryBtn, ...(generating || !summary.trim() ? s.btnDisabled : {}) }}
          onClick={handleGenerate}
          disabled={generating || !summary.trim()}
        >
          {generating ? 'Populating…' : (hasGenerated ? '↻ Regenerate with AI' : '✨ Populate with AI')}
        </button>
      </div>

      {/* Editor — the same rich text editor as the Jira issue manager. Starts
          from the PM ticket's data, updated in place by AI, then hand-edited. */}
      <div style={s.editWrap}>
        <label style={s.label}>Epic description</label>
        <p style={s.help}>
          Loaded from the Idea. Use <strong>Populate with AI</strong> to draft the Epic from the ticket, edit here, then create.
        </p>
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <RichTextEditor
              ref={editorRef}
              value={description}
              onChange={setDescription}
              placeholder="Epic description…"
              disabled={ideaLoading || generating}
            />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <LyricAIPopup
              selectedModel={selectedModel}
              currentValue={description}
              onInsert={(value) => {
                const ed = editorRef.current;
                if (ed && ed.hasSelection && ed.hasSelection() && ed.replaceSelectedText) {
                  ed.replaceSelectedText(value);
                } else {
                  setDescription((prev) => (prev || '') + '<br/><br/>' + value);
                }
              }}
              getSelectedText={() => {
                const ed = editorRef.current;
                return ed && ed.getSelectedText ? ed.getSelectedText() : '';
              }}
            />
          </div>
        </div>
      </div>

      {error && <div style={s.error}>{error}</div>}

      {/* Create */}
      <div style={s.actionRow}>
        <button
          style={{ ...s.createBtn, ...(creating || !summary.trim() || !description.trim() || !selectedProject ? s.btnDisabled : {}) }}
          onClick={handleCreate}
          disabled={creating || !summary.trim() || !description.trim() || !selectedProject}
        >
          {creating ? 'Creating…' : '🚀 Create EPIC'}
        </button>
      </div>

      {result && (
        <div style={s.success}>
          ✓ Created <strong><JiraTicketLink ticketKey={result.epicKey} href={result.epicLink} /></strong> and linked it into this flow.
          {result.epicLink && <> — <a href={result.epicLink} target="_blank" rel="noreferrer">Open in Jira ↗</a></>}
        </div>
      )}
    </div>
  );
};

const s = {
  wrap: { marginTop: 14, borderTop: '1px dashed #d9d9d9', paddingTop: 14 },
  refNote: { background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '10px 12px', fontSize: 13, color: '#166534', marginBottom: 12 },
  hintBox: { marginTop: 14, background: '#fffbeb', border: '1px solid #fde68a', color: '#92400e', borderRadius: 8, padding: '10px 12px', fontSize: 13 },
  label: { display: 'block', fontWeight: 600, marginBottom: 6, fontSize: 14, color: '#333' },
  input: { width: '100%', padding: '8px 10px', border: '1px solid #ccc', borderRadius: 6, fontSize: 14, boxSizing: 'border-box' },
  readonly: { padding: '8px 10px', border: '1px solid #eee', borderRadius: 6, fontSize: 14, background: '#f9fafb', color: '#333' },
  ruleRow: { display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' },
  actionRow: { marginTop: 12 },
  editWrap: { marginTop: 12 },
  primaryBtn: { background: '#1a56db', color: '#fff', border: 'none', padding: '9px 16px', borderRadius: 6, fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  createBtn: { background: '#047857', color: '#fff', border: 'none', padding: '10px 18px', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  secondaryBtn: { background: '#fff', border: '1px solid #ccc', padding: '8px 12px', borderRadius: 6, fontSize: 13, cursor: 'pointer', color: '#333' },
  btnDisabled: { opacity: 0.6, cursor: 'not-allowed' },
  help: { color: '#777', fontSize: 12, margin: '6px 0 0' },
  chipWrap: { display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 },
  chip: { display: 'inline-flex', alignItems: 'center', gap: 6, background: '#e8f0fe', color: '#1a56db', padding: '4px 8px', borderRadius: 12, fontSize: 13 },
  chipX: { background: 'none', border: 'none', color: '#1a56db', cursor: 'pointer', fontSize: 15, lineHeight: 1, padding: 0 },
  error: { background: '#fde8e8', color: '#9b1c1c', padding: '10px 12px', borderRadius: 6, margin: '10px 0', fontSize: 14 },
  success: { background: '#ecfdf5', border: '1px solid #a7f3d0', color: '#065f46', padding: '10px 12px', borderRadius: 8, marginTop: 12, fontSize: 14 },
};

export default CreateEpicPanel;
